import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { 
  ArrowLeft, Copy, Share2, Users, Gift, Check, Loader2,
  Trophy, Star, Crown, Heart, Sparkles, TrendingUp,
  ChevronRight, Award, Zap, Target, PartyPopper
} from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";

const ReferralPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [referralCode, setReferralCode] = useState("");
  const [shareLink, setShareLink] = useState("");
  const [stats, setStats] = useState(null);
  const [impact, setImpact] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationData, setCelebrationData] = useState(null);

  // Confetti burst function
  const triggerConfetti = useCallback((type = "default") => {
    const defaults = {
      origin: { y: 0.7 },
      zIndex: 9999
    };

    if (type === "milestone") {
      // Big celebration for milestones
      const count = 200;
      const confettiDefaults = {
        ...defaults,
        spread: 100,
        ticks: 200,
        gravity: 0.8,
        decay: 0.94,
        startVelocity: 40,
        colors: ['#1C3A7A', '#FAB710', '#22c55e', '#ec4899', '#8b5cf6']
      };

      function fire(particleRatio, opts) {
        confetti({
          ...confettiDefaults,
          particleCount: Math.floor(count * particleRatio),
          ...opts
        });
      }

      fire(0.25, { spread: 26, startVelocity: 55 });
      fire(0.2, { spread: 60 });
      fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
      fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
      fire(0.1, { spread: 120, startVelocity: 45 });
    } else if (type === "copy") {
      // Small burst for copy
      confetti({
        ...defaults,
        particleCount: 30,
        spread: 50,
        startVelocity: 25,
        colors: ['#FAB710', '#1C3A7A'],
        origin: { x: 0.8, y: 0.5 }
      });
    } else if (type === "share") {
      // Hearts and stars for sharing
      const shapes = ['circle', 'square'];
      confetti({
        ...defaults,
        particleCount: 50,
        spread: 80,
        startVelocity: 35,
        colors: ['#ec4899', '#f43f5e', '#FAB710'],
        shapes: shapes,
        origin: { x: 0.5, y: 0.6 }
      });
    } else {
      // Default celebration
      confetti({
        ...defaults,
        particleCount: 100,
        spread: 70,
        colors: ['#1C3A7A', '#FAB710', '#22c55e']
      });
    }
  }, []);

  // Check for newly achieved milestones
  const checkMilestoneAchievement = useCallback((newStats) => {
    if (!newStats?.milestones) return;
    
    // Check if there's a milestone just achieved (looking for latest)
    const justAchieved = newStats.milestones.find(m => 
      m.achieved && m.progress === 100 && !localStorage.getItem(`milestone_celebrated_${m.badge}`)
    );
    
    if (justAchieved) {
      localStorage.setItem(`milestone_celebrated_${justAchieved.badge}`, 'true');
      setCelebrationData(justAchieved);
      setShowCelebration(true);
      triggerConfetti("milestone");
      
      // Play celebration sound (optional - uses Web Audio API)
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        oscillator.frequency.setValueAtTime(523.25, audioContext.currentTime); // C5
        gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.1);
      } catch (e) {
        // Audio not supported
      }
    }
  }, [triggerConfetti]);

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      const [codeRes, statsRes, impactRes, leaderboardRes] = await Promise.all([
        api.getReferralCode(),
        api.getReferralStats(),
        api.getReferralImpact(),
        api.getReferralLeaderboard()
      ]);
      setReferralCode(codeRes.data.referral_code);
      setShareLink(codeRes.data.share_link);
      setStats(statsRes.data);
      setImpact(impactRes.data);
      setLeaderboard(leaderboardRes.data);
      
      // Check for milestone achievements
      checkMilestoneAchievement(statsRes.data);
    } catch (error) {
      console.error("Failed to fetch referral data:", error);
      toast.error("Failed to load referral data");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(referralCode);
      setCopied(true);
      triggerConfetti("copy");
      toast.success("Code copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy code");
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: "Join RecoveryMatch!",
      text: `Join me on RecoveryMatch - intentional dating for people in recovery. Use my code ${referralCode} to get 1 week of Recovery+ free!`,
      url: shareLink,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        triggerConfetti("share");
      } catch (error) {
        handleCopyCode();
      }
    } else {
      handleCopyCode();
    }
  };

  // Demo function to trigger celebration (for testing)
  const triggerDemoCelebration = () => {
    setCelebrationData({
      name: "Community Builder",
      emoji: "🌱",
      perk: "Unlocked: Profile Badge + 2 Extra Daily Matches"
    });
    setShowCelebration(true);
    triggerConfetti("milestone");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="referral-loading">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        >
          <Loader2 className="w-8 h-8 text-[#FAB710]" />
        </motion.div>
      </div>
    );
  }

  const badgeConfig = {
    connector: { emoji: "🤝", color: "bg-blue-500", name: "Connector" },
    community_builder: { emoji: "🌱", color: "bg-green-500", name: "Community Builder" },
    recovery_champion: { emoji: "🏆", color: "bg-[#FAB710]", name: "Recovery Champion" }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24" data-testid="referral-page">
      {/* Celebration Modal */}
      <AnimatePresence>
        {showCelebration && celebrationData && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowCelebration(false)}
          >
            <motion.div
              initial={{ scale: 0.5, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.5, opacity: 0, y: 50 }}
              transition={{ type: "spring", damping: 15 }}
              className="bg-white rounded-3xl p-8 max-w-sm w-full text-center shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="celebration-modal"
            >
              <motion.div
                animate={{ 
                  scale: [1, 1.2, 1],
                  rotate: [0, -10, 10, -10, 0]
                }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-7xl mb-4"
              >
                {celebrationData.emoji}
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                  Milestone Achieved!
                </h2>
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#FAB710]/20 to-[#FAB710]/30 px-4 py-2 rounded-full mb-4">
                  <Trophy className="w-5 h-5 text-[#FAB710]" />
                  <span className="font-bold text-[#1C3A7A]">{celebrationData.name}</span>
                </div>
                <p className="text-gray-600 mb-6">{celebrationData.perk}</p>
              </motion.div>

              <motion.div
                className="flex gap-3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setShowCelebration(false);
                    handleShare();
                  }}
                  className="flex-1 bg-gradient-to-r from-[#FAB710] to-[#f59e0b] text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2"
                >
                  <Share2 className="w-5 h-5" />
                  Share Win
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowCelebration(false)}
                  className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold"
                >
                  Continue
                </motion.button>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="bg-gradient-to-br from-[#1C3A7A] via-[#2a4f9e] to-[#1C3A7A] text-white">
        <div className="max-w-md mx-auto px-4 py-4 flex items-center justify-between">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-white/10 rounded-full transition"
            data-testid="referral-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </motion.button>
          <h1 className="text-xl font-bold flex items-center gap-2" data-testid="referral-title">
            <motion.div
              animate={{ rotate: [0, -15, 15, 0] }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
            >
              <Trophy className="w-6 h-6 text-[#FAB710]" />
            </motion.div>
            Community Champion
          </h1>
          <div className="w-9" />
        </div>

        {/* Hero Section with Current Badge */}
        <div className="max-w-md mx-auto px-4 pb-8 pt-2 text-center">
          {stats?.current_badge ? (
            <motion.div 
              className="mb-4"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", damping: 10 }}
            >
              <motion.div 
                className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full cursor-pointer"
                whileHover={{ scale: 1.05 }}
                onClick={triggerDemoCelebration}
              >
                <motion.span 
                  className="text-2xl"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  {badgeConfig[stats.current_badge.badge]?.emoji}
                </motion.span>
                <span className="font-semibold">{stats.current_badge.name}</span>
              </motion.div>
            </motion.div>
          ) : (
            <motion.div 
              className="text-6xl mb-4"
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              🎁
            </motion.div>
          )}
          <motion.h2 
            className="text-2xl font-bold mb-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            Grow the Community
          </motion.h2>
          <motion.p 
            className="text-white font-semibold text-base mt-3 bg-white/10 inline-block px-4 py-2 rounded-full"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            Refer 10 people and they refer 10 people... our community grows. 🌱
          </motion.p>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 -mt-4">
        {/* Tab Navigation */}
        <motion.div 
          className="bg-white rounded-2xl shadow-lg p-1 mb-4 flex gap-1"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {["overview", "milestones", "impact"].map((tab, idx) => (
            <motion.button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-2 px-3 rounded-xl text-sm font-medium transition-all ${
                activeTab === tab 
                  ? "bg-[#1C3A7A] text-white shadow-md" 
                  : "text-gray-600 hover:bg-gray-100"
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid={`tab-${tab}`}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.1 }}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </motion.button>
          ))}
        </motion.div>

        {/* Overview Tab */}
        <AnimatePresence mode="wait">
          {activeTab === "overview" && (
            <motion.div
              key="overview"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              {/* Referral Code Card */}
              <motion.div 
                className="bg-white rounded-2xl shadow-lg p-6 mb-4" 
                data-testid="referral-code-card"
                whileHover={{ y: -2 }}
              >
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Your Referral Code</h3>
                <div className="flex items-center gap-3">
                  <motion.div 
                    className="flex-1 bg-gradient-to-r from-[#1C3A7A]/5 to-[#FAB710]/10 rounded-xl px-4 py-3 font-mono text-xl font-bold text-[#1C3A7A] tracking-wider text-center border-2 border-dashed border-[#1C3A7A]/20"
                    whileHover={{ scale: 1.02 }}
                  >
                    {referralCode}
                  </motion.div>
                  <motion.button
                    onClick={handleCopyCode}
                    className={`p-3 rounded-xl transition-all ${
                      copied ? "bg-green-500 text-white" : "bg-[#1C3A7A] text-white hover:bg-[#2a4f9e]"
                    }`}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    data-testid="copy-code-btn"
                  >
                    <AnimatePresence mode="wait">
                      {copied ? (
                        <motion.div
                          key="check"
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          exit={{ scale: 0 }}
                        >
                          <Check className="w-5 h-5" />
                        </motion.div>
                      ) : (
                        <motion.div
                          key="copy"
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          exit={{ scale: 0 }}
                        >
                          <Copy className="w-5 h-5" />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.button>
                </div>
                <motion.button
                  onClick={handleShare}
                  className="w-full mt-4 flex items-center justify-center gap-2 bg-gradient-to-r from-[#FAB710] to-[#f59e0b] text-white py-3 rounded-xl font-semibold shadow-md"
                  whileHover={{ scale: 1.02, boxShadow: "0 10px 25px -5px rgba(250, 183, 16, 0.4)" }}
                  whileTap={{ scale: 0.98 }}
                  data-testid="share-btn"
                >
                  <Share2 className="w-5 h-5" />
                  Share with Friends
                  <motion.span
                    animate={{ scale: [1, 1.3, 1] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    ✨
                  </motion.span>
                </motion.button>
              </motion.div>

              {/* Instant Rewards Info */}
              <motion.div 
                className="bg-white rounded-2xl shadow-lg p-6 mb-4" 
                data-testid="rewards-info"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4 flex items-center gap-2">
                  <motion.div
                    animate={{ rotate: [0, 360] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                  >
                    <Zap className="w-4 h-4 text-[#FAB710]" />
                  </motion.div>
                  Instant Rewards
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <motion.div 
                    className="bg-gradient-to-br from-[#1C3A7A]/5 to-[#1C3A7A]/10 rounded-xl p-4 text-center"
                    whileHover={{ scale: 1.05, y: -5 }}
                  >
                    <motion.div 
                      className="text-3xl mb-2"
                      animate={{ rotate: [0, -10, 10, 0] }}
                      transition={{ duration: 2, repeat: Infinity, repeatDelay: 2 }}
                    >
                      👑
                    </motion.div>
                    <p className="text-sm font-medium text-gray-700">You Get</p>
                    <p className="text-lg font-bold text-[#1C3A7A]">
                      {stats?.rewards?.referrer_premium_days || 7} days
                    </p>
                    <p className="text-xs text-gray-500">Recovery+ free</p>
                  </motion.div>
                  <motion.div 
                    className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 text-center"
                    whileHover={{ scale: 1.05, y: -5 }}
                  >
                    <motion.div 
                      className="text-3xl mb-2"
                      animate={{ y: [0, -5, 0] }}
                      transition={{ duration: 1, repeat: Infinity }}
                    >
                      🎉
                    </motion.div>
                    <p className="text-sm font-medium text-gray-700">Friend Gets</p>
                    <p className="text-lg font-bold text-green-600">
                      {stats?.rewards?.friend_premium_days || 7} days
                    </p>
                    <p className="text-xs text-gray-500">Recovery+ free</p>
                  </motion.div>
                </div>
                <p className="text-xs text-gray-400 text-center mt-3">
                  Rewards granted when your friend completes their profile
                </p>
              </motion.div>

              {/* Stats */}
              <motion.div 
                className="bg-white rounded-2xl shadow-lg p-6 mb-4" 
                data-testid="referral-stats"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">Your Stats</h3>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: stats?.total_referrals || 0, label: "Total", color: "from-blue-50 to-blue-100", textColor: "text-[#1C3A7A]" },
                    { value: stats?.total_premium_days_earned || 0, label: "Days Earned", color: "from-green-50 to-green-100", textColor: "text-green-600" },
                    { value: stats?.connections_made_by_referrals || 0, label: "Matches", color: "from-purple-50 to-purple-100", textColor: "text-purple-600" }
                  ].map((stat, idx) => (
                    <motion.div 
                      key={idx}
                      className={`bg-gradient-to-br ${stat.color} rounded-xl p-3 text-center`}
                      whileHover={{ scale: 1.1 }}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.3 + idx * 0.1 }}
                    >
                      <motion.p 
                        className={`text-2xl font-bold ${stat.textColor}`}
                        key={stat.value}
                        initial={{ scale: 1.5 }}
                        animate={{ scale: 1 }}
                      >
                        {stat.value}
                      </motion.p>
                      <p className="text-xs text-gray-600">{stat.label}</p>
                    </motion.div>
                  ))}
                </div>
                
                {/* Progress to next milestone */}
                {stats?.next_milestone && (
                  <motion.div 
                    className="mt-4 p-3 bg-gradient-to-r from-[#FAB710]/10 to-[#FAB710]/20 rounded-xl"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700 flex items-center gap-1">
                        Next: <span className="text-lg">{stats.next_milestone.emoji}</span> {stats.next_milestone.badge}
                      </span>
                      <motion.span 
                        className="text-sm font-bold text-[#FAB710]"
                        animate={{ scale: [1, 1.1, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      >
                        {stats.next_milestone.remaining} more
                      </motion.span>
                    </div>
                    <div className="w-full bg-white rounded-full h-2 overflow-hidden">
                      <motion.div 
                        className="bg-gradient-to-r from-[#FAB710] to-[#f59e0b] h-2 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ 
                          width: `${((stats.total_referrals || 0) / stats.next_milestone.threshold) * 100}%` 
                        }}
                        transition={{ duration: 1, ease: "easeOut" }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{stats.next_milestone.perk}</p>
                  </motion.div>
                )}
              </motion.div>

              {/* Leaderboard Preview */}
              {leaderboard?.leaderboard?.length > 0 && (
                <motion.div 
                  className="bg-white rounded-2xl shadow-lg p-6 mb-4" 
                  data-testid="leaderboard-preview"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-gray-500 uppercase flex items-center gap-2">
                      <motion.div
                        animate={{ rotate: [0, -10, 10, 0] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      >
                        <Crown className="w-4 h-4 text-[#FAB710]" />
                      </motion.div>
                      Top Champions
                    </h3>
                    <span className="text-xs text-gray-400">{leaderboard.period}</span>
                  </div>
                  <div className="space-y-2">
                    {leaderboard.leaderboard.slice(0, 3).map((person, idx) => (
                      <motion.div 
                        key={idx} 
                        className={`flex items-center justify-between p-3 rounded-xl ${
                          idx === 0 ? "bg-[#FAB710]/10" : "bg-gray-50"
                        }`}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4 + idx * 0.1 }}
                        whileHover={{ scale: 1.02, x: 5 }}
                      >
                        <div className="flex items-center gap-3">
                          <motion.span 
                            className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                              idx === 0 ? "bg-[#FAB710] text-white" : 
                              idx === 1 ? "bg-gray-300 text-gray-700" :
                              "bg-orange-200 text-orange-700"
                            }`}
                            whileHover={{ scale: 1.2, rotate: 360 }}
                          >
                            {idx + 1}
                          </motion.span>
                          <div>
                            <p className="font-medium text-gray-800">{person.name}</p>
                            {person.badge && (
                              <span className="text-xs text-gray-500">{person.badge.emoji} {person.badge.name}</span>
                            )}
                          </div>
                        </div>
                        <span className="font-bold text-[#1C3A7A]">{person.referrals}</span>
                      </motion.div>
                    ))}
                  </div>
                  {leaderboard.user_rank && (
                    <motion.div 
                      className="mt-3 pt-3 border-t text-center"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.7 }}
                    >
                      <span className="text-sm text-gray-500">Your rank: </span>
                      <motion.span 
                        className="font-bold text-[#1C3A7A]"
                        animate={{ scale: [1, 1.1, 1] }}
                        transition={{ duration: 1, repeat: Infinity, repeatDelay: 2 }}
                      >
                        #{leaderboard.user_rank}
                      </motion.span>
                    </motion.div>
                  )}
                </motion.div>
              )}

              {/* Recent Referrals */}
              {stats?.history && stats.history.length > 0 && (
                <motion.div 
                  className="bg-white rounded-2xl shadow-lg p-6" 
                  data-testid="referral-history"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">Recent Referrals</h3>
                  <div className="space-y-3">
                    {stats.history.slice(0, 5).map((referral, index) => (
                      <motion.div 
                        key={index} 
                        className="flex items-center justify-between py-2 border-b last:border-0"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.5 + index * 0.1 }}
                        whileHover={{ x: 5 }}
                      >
                        <div className="flex items-center gap-3">
                          <motion.div 
                            className="w-10 h-10 bg-gradient-to-br from-green-400 to-green-500 rounded-full flex items-center justify-center text-white font-bold"
                            whileHover={{ rotate: 360 }}
                            transition={{ duration: 0.5 }}
                          >
                            {referral.referred_user_name?.charAt(0) || "?"}
                          </motion.div>
                          <div>
                            <p className="font-medium text-gray-800">{referral.referred_user_name}</p>
                            <p className="text-xs text-gray-500">
                              {new Date(referral.completed_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <motion.p 
                            className="text-sm font-bold text-green-500"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", delay: 0.6 + index * 0.1 }}
                          >
                            +{referral.premium_days_awarded}d
                          </motion.p>
                          <p className="text-xs text-gray-400">Recovery+</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Empty State */}
              {(!stats?.history || stats.history.length === 0) && (
                <motion.div 
                  className="bg-white rounded-2xl shadow-lg p-6 text-center" 
                  data-testid="referral-empty"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                >
                  <motion.div 
                    className="text-5xl mb-4"
                    animate={{ rotate: [0, -10, 10, 0], scale: [1, 1.1, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    🌟
                  </motion.div>
                  <h4 className="font-bold text-gray-800 mb-2">Start Your Journey</h4>
                  <p className="text-gray-500 text-sm mb-4">
                    Share your code and help others find meaningful connections in recovery.
                  </p>
                  <motion.button
                    onClick={handleShare}
                    className="inline-flex items-center gap-2 bg-[#1C3A7A] text-white px-6 py-2 rounded-xl font-medium"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Share2 className="w-4 h-4" />
                    Invite Your First Friend
                  </motion.button>
                </motion.div>
              )}
            </motion.div>
          )}

          {/* Milestones Tab */}
          {activeTab === "milestones" && (
            <motion.div
              key="milestones"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="bg-white rounded-2xl shadow-lg p-6 mb-4" 
              data-testid="milestones-tab"
            >
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <motion.div
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                >
                  <Award className="w-5 h-5 text-[#FAB710]" />
                </motion.div>
                Milestone Badges
              </h3>
              <div className="space-y-4">
                {stats?.milestones?.map((milestone, idx) => (
                  <motion.div 
                    key={idx}
                    className={`relative p-4 rounded-xl border-2 transition-all ${
                      milestone.achieved 
                        ? "border-green-400 bg-green-50" 
                        : "border-gray-200 bg-gray-50"
                    }`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.15 }}
                    whileHover={{ scale: 1.02, y: -2 }}
                    onClick={() => {
                      if (milestone.achieved) {
                        setCelebrationData(milestone);
                        setShowCelebration(true);
                        triggerConfetti("milestone");
                      }
                    }}
                  >
                    {milestone.achieved && (
                      <motion.div
                        className="absolute -top-2 -right-2"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", delay: idx * 0.15 + 0.3 }}
                      >
                        <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                          <Check className="w-5 h-5 text-white" />
                        </div>
                      </motion.div>
                    )}
                    <div className="flex items-start gap-4">
                      <motion.div 
                        className={`w-14 h-14 rounded-xl flex items-center justify-center text-2xl ${
                          milestone.achieved ? "bg-green-500" : "bg-gray-200"
                        }`}
                        animate={milestone.achieved ? { 
                          scale: [1, 1.1, 1],
                          rotate: [0, -5, 5, 0]
                        } : {}}
                        transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                      >
                        {milestone.emoji}
                      </motion.div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className="font-bold text-gray-800">{milestone.name}</h4>
                          {milestone.achieved && (
                            <motion.span 
                              className="text-xs bg-green-500 text-white px-2 py-1 rounded-full flex items-center gap-1"
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{ type: "spring" }}
                            >
                              <Sparkles className="w-3 h-3" />
                              Achieved
                            </motion.span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{milestone.perk}</p>
                        <div className="mt-2">
                          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                            <span>{stats?.total_referrals || 0} / {milestone.threshold} referrals</span>
                            <span>{milestone.progress}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                            <motion.div 
                              className={`h-2 rounded-full ${
                                milestone.achieved ? "bg-green-500" : "bg-[#FAB710]"
                              }`}
                              initial={{ width: 0 }}
                              animate={{ width: `${milestone.progress}%` }}
                              transition={{ duration: 1, ease: "easeOut", delay: idx * 0.15 }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
              
              {/* Hint to tap achieved badges */}
              <motion.p 
                className="text-xs text-center text-gray-400 mt-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
              >
                💡 Tap achieved badges to celebrate again!
              </motion.p>
            </motion.div>
          )}

          {/* Impact Tab */}
          {activeTab === "impact" && (
            <motion.div
              key="impact"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <motion.div 
                className="bg-white rounded-2xl shadow-lg p-6 mb-4" 
                data-testid="impact-tab"
              >
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <motion.div
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 1, repeat: Infinity }}
                  >
                    <Heart className="w-5 h-5 text-pink-500" />
                  </motion.div>
                  Your Impact
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  See the difference you&apos;ve made in the recovery community.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <motion.div 
                    className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 text-center"
                    whileHover={{ scale: 1.05, y: -5 }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <motion.div
                      animate={{ y: [0, -3, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      <Users className="w-8 h-8 mx-auto text-[#1C3A7A] mb-2" />
                    </motion.div>
                    <p className="text-2xl font-bold text-[#1C3A7A]">{impact?.total_referrals || 0}</p>
                    <p className="text-xs text-gray-600">People Invited</p>
                  </motion.div>
                  <motion.div 
                    className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-xl p-4 text-center"
                    whileHover={{ scale: 1.05, y: -5 }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    <motion.div
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 1, repeat: Infinity }}
                    >
                      <Heart className="w-8 h-8 mx-auto text-pink-500 mb-2" />
                    </motion.div>
                    <p className="text-2xl font-bold text-pink-500">{impact?.mutual_matches || 0}</p>
                    <p className="text-xs text-gray-600">Connections Made</p>
                  </motion.div>
                </div>
                <motion.div 
                  className="mt-4 bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 text-center"
                  whileHover={{ scale: 1.03 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <motion.div
                    animate={{ rotate: [0, 10, -10, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <Sparkles className="w-8 h-8 mx-auto text-green-500 mb-2" />
                  </motion.div>
                  <p className="text-2xl font-bold text-green-500">{impact?.conversations_started || 0}</p>
                  <p className="text-xs text-gray-600">Conversations Started</p>
                </motion.div>
              </motion.div>

              {/* Success Stories */}
              {impact?.success_stories?.length > 0 && (
                <motion.div 
                  className="bg-white rounded-2xl shadow-lg p-6 mb-4" 
                  data-testid="success-stories"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4 flex items-center gap-2">
                    <Star className="w-4 h-4 text-[#FAB710]" />
                    Recent Wins
                  </h3>
                  <div className="space-y-3">
                    {impact.success_stories.map((story, idx) => (
                      <motion.div 
                        key={idx} 
                        className="flex items-center gap-3 p-3 bg-green-50 rounded-xl"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4 + idx * 0.1 }}
                        whileHover={{ scale: 1.02, x: 5 }}
                      >
                        <motion.div 
                          className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center"
                          animate={{ rotate: [0, 10, -10, 0] }}
                          transition={{ duration: 2, repeat: Infinity, delay: idx * 0.5 }}
                        >
                          <span className="text-lg">💕</span>
                        </motion.div>
                        <div>
                          <p className="font-medium text-gray-800">{story.name}</p>
                          <p className="text-sm text-green-600">{story.message}</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Motivational Message */}
              <motion.div 
                className="bg-gradient-to-br from-[#1C3A7A] to-[#2a4f9e] rounded-2xl p-6 text-white text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                whileHover={{ scale: 1.02 }}
              >
                <motion.div
                  animate={{ y: [0, -5, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <TrendingUp className="w-10 h-10 mx-auto mb-3 text-[#FAB710]" />
                </motion.div>
                <h4 className="font-bold text-lg mb-2">Keep Growing the Community!</h4>
                <p className="text-white/80 text-sm">
                  Every person you invite is another chance for someone in recovery to find meaningful connection.
                </p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ReferralPage;
